import { createApiRef, DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';

export interface GithubSecretsApi {
  // Secrets endpoints
  listOrgSecrets(org: string): Promise<any>;
  getOrgSecret(org: string, secretName: string): Promise<any>;
  createOrUpdateOrgSecret(
    org: string,
    secretName: string,
    data: { encrypted_value?: string; key_id?: string; visibility?: string; selected_repository_ids?: number[] }
  ): Promise<void>;
  deleteOrgSecret(org: string, secretName: string): Promise<void>;
  getOrgPublicKey(org: string): Promise<{ key: string; key_id: string }>;
  listSecretRepositories(org: string, secretName: string): Promise<any>;
  setSecretRepositories(org: string, secretName: string, repositoryIds: number[]): Promise<void>;
  addRepositoryToSecret(org: string, secretName: string, repositoryId: number): Promise<void>;
  removeRepositoryFromSecret(org: string, secretName: string, repositoryId: number): Promise<void>;
  listOrgRepositories(org: string): Promise<any>;

  // Variables endpoints
  listOrgVariables(org: string): Promise<any>;
  getOrgVariable(org: string, variableName: string): Promise<any>;
  createOrgVariable(
    org: string,
    data: { name: string; value: string; visibility: string; selected_repository_ids?: number[] }
  ): Promise<void>;
  updateOrgVariable(
    org: string,
    variableName: string,
    data: { value?: string; visibility?: string; selected_repository_ids?: number[] }
  ): Promise<void>;
  deleteOrgVariable(org: string, variableName: string): Promise<void>;
  listVariableRepositories(org: string, variableName: string): Promise<any>;
  setVariableRepositories(org: string, variableName: string, repositoryIds: number[]): Promise<void>;
  addRepositoryToVariable(org: string, variableName: string, repositoryId: number): Promise<void>;
  removeRepositoryFromVariable(org: string, variableName: string, repositoryId: number): Promise<void>;
}

export const githubSecretsApiRef = createApiRef<GithubSecretsApi>({
  id: 'plugin.github-secrets.service',
});

export class GithubSecretsClient implements GithubSecretsApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(options: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
  }

  private async fetch(path: string, init?: RequestInit): Promise<Response> {
    const baseUrl = await this.discoveryApi.getBaseUrl('github-secrets-api');
    return this.fetchApi.fetch(`${baseUrl}${path}`, init);
  }

  async listOrgSecrets(org: string): Promise<any> {
    const response = await this.fetch(`/orgs/${org}/secrets`);
    if (!response.ok) {
      throw new Error(`Failed to list secrets: ${response.statusText}`);
    }
    return response.json();
  }

  async getOrgSecret(org: string, secretName: string): Promise<any> {
    const response = await this.fetch(`/orgs/${org}/secrets/${secretName}`);
    if (!response.ok) {
      throw new Error(`Failed to get secret: ${response.statusText}`);
    }
    return response.json();
  }

  async createOrUpdateOrgSecret(
    org: string,
    secretName: string,
    data: { encrypted_value?: string; key_id?: string; visibility: string; selected_repository_ids?: number[] }
  ): Promise<void> {
    const response = await this.fetch(`/orgs/${org}/secrets/${secretName}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      throw new Error(`Failed to create/update secret: ${response.statusText}`);
    }
  }

  async deleteOrgSecret(org: string, secretName: string): Promise<void> {
    const response = await this.fetch(`/orgs/${org}/secrets/${secretName}`, {
      method: 'DELETE',
    });
    if (!response.ok) {
      throw new Error(`Failed to delete secret: ${response.statusText}`);
    }
  }

  async getOrgPublicKey(org: string): Promise<{ key: string; key_id: string }> {
    const response = await this.fetch(`/orgs/${org}/secrets/public-key`);
    if (!response.ok) {
      throw new Error(`Failed to get public key: ${response.statusText}`);
    }
    return response.json();
  }

  async listSecretRepositories(org: string, secretName: string): Promise<any> {
    const response = await this.fetch(`/orgs/${org}/secrets/${secretName}/repositories`);
    if (!response.ok) {
      throw new Error(`Failed to list secret repositories: ${response.statusText}`);
    }
    return response.json();
  }

  async setSecretRepositories(org: string, secretName: string, repositoryIds: number[]): Promise<void> {
    const response = await this.fetch(`/orgs/${org}/secrets/${secretName}/repositories`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ selected_repository_ids: repositoryIds }),
    });
    if (!response.ok) {
      throw new Error(`Failed to set secret repositories: ${response.statusText}`);
    }
  }

  async addRepositoryToSecret(org: string, secretName: string, repositoryId: number): Promise<void> {
    const response = await this.fetch(`/orgs/${org}/secrets/${secretName}/repositories/${repositoryId}`, {
      method: 'PUT',
    });
    if (!response.ok) {
      throw new Error(`Failed to add repository: ${response.statusText}`);
    }
  }

  async removeRepositoryFromSecret(org: string, secretName: string, repositoryId: number): Promise<void> {
    const response = await this.fetch(`/orgs/${org}/secrets/${secretName}/repositories/${repositoryId}`, {
      method: 'DELETE',
    });
    if (!response.ok) {
      throw new Error(`Failed to remove repository: ${response.statusText}`);
    }
  }

  async listOrgRepositories(org: string): Promise<any> {
    const response = await this.fetch(`/orgs/${org}/repos?per_page=100`);
    if (!response.ok) {
      throw new Error(`Failed to list repositories: ${response.statusText}`);
    }
    return response.json();
  }

  // ============================================================================
  // VARIABLES METHODS
  // ============================================================================

  async listOrgVariables(org: string): Promise<any> {
    const response = await this.fetch(`/orgs/${org}/variables`);
    if (!response.ok) {
      throw new Error(`Failed to list variables: ${response.statusText}`);
    }
    return response.json();
  }

  async getOrgVariable(org: string, variableName: string): Promise<any> {
    const response = await this.fetch(`/orgs/${org}/variables/${variableName}`);
    if (!response.ok) {
      throw new Error(`Failed to get variable: ${response.statusText}`);
    }
    return response.json();
  }

  async createOrgVariable(
    org: string,
    data: { name: string; value: string; visibility: string; selected_repository_ids?: number[] }
  ): Promise<void> {
    const response = await this.fetch(`/orgs/${org}/variables`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      throw new Error(`Failed to create variable: ${response.statusText}`);
    }
  }

  async updateOrgVariable(
    org: string,
    variableName: string,
    data: { value?: string; visibility?: string; selected_repository_ids?: number[] }
  ): Promise<void> {
    const response = await this.fetch(`/orgs/${org}/variables/${variableName}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      throw new Error(`Failed to update variable: ${response.statusText}`);
    }
  }

  async deleteOrgVariable(org: string, variableName: string): Promise<void> {
    const response = await this.fetch(`/orgs/${org}/variables/${variableName}`, {
      method: 'DELETE',
    });
    if (!response.ok) {
      throw new Error(`Failed to delete variable: ${response.statusText}`);
    }
  }

  async listVariableRepositories(org: string, variableName: string): Promise<any> {
    const response = await this.fetch(`/orgs/${org}/variables/${variableName}/repositories`);
    if (!response.ok) {
      throw new Error(`Failed to list variable repositories: ${response.statusText}`);
    }
    return response.json();
  }

  async setVariableRepositories(org: string, variableName: string, repositoryIds: number[]): Promise<void> {
    const response = await this.fetch(`/orgs/${org}/variables/${variableName}/repositories`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ selected_repository_ids: repositoryIds }),
    });
    if (!response.ok) {
      throw new Error(`Failed to set variable repositories: ${response.statusText}`);
    }
  }

  async addRepositoryToVariable(org: string, variableName: string, repositoryId: number): Promise<void> {
    const response = await this.fetch(`/orgs/${org}/variables/${variableName}/repositories/${repositoryId}`, {
      method: 'PUT',
    });
    if (!response.ok) {
      throw new Error(`Failed to add repository to variable: ${response.statusText}`);
    }
  }

  async removeRepositoryFromVariable(org: string, variableName: string, repositoryId: number): Promise<void> {
    const response = await this.fetch(`/orgs/${org}/variables/${variableName}/repositories/${repositoryId}`, {
      method: 'DELETE',
    });
    if (!response.ok) {
      throw new Error(`Failed to remove repository from variable: ${response.statusText}`);
    }
  }
}
